import { HttpParams } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { Tierarzt } from '../../../../../projects/kiene-core/src/lib/components/tieraerzte/classes/tierarzt';
import { KieneTableService } from '../../../components/kiene-table/kiene-table.service';
import {
    FIBU_HONORAR_DEFAULT_KOSTENARTEN,
    FibuHonorarKostenart,
    FibuHonorarTa,
} from '../fibu-honorar-ta';

@Component({
    selector: 'kiene-fibu-honorar-ta-dialog',
    templateUrl: './fibu-honorar-ta-dialog.component.html',
    styleUrls: ['./fibu-honorar-ta-dialog.component.scss'],
})
export class FibuHonorarTaDialogComponent implements OnInit {
    isEdit = false;
    defaultKostenarten = FIBU_HONORAR_DEFAULT_KOSTENARTEN;

    bezeichnungCtrl = new UntypedFormControl('', [Validators.required]);
    suffixCtrl = new UntypedFormControl('', [Validators.required]);
    aktivCtrl = new UntypedFormControl(true);
    kommentarCtrl = new UntypedFormControl('');

    tierarzt: Tierarzt = null;
    suffixBySachkonto: Record<string, string> = {};
    extraSachkontoCtrl = new UntypedFormControl('');
    extraSuffixCtrl = new UntypedFormControl('');

    private fibuHonorarTaId: number = null;
    kontoStatus: Record<string, string> = {};

    constructor(
        public dialogRef: MatDialogRef<FibuHonorarTaDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private data: { honorarTa: FibuHonorarTa },
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        private api: KieneTableService,
        private messageService: MessageService,
    ) {}

    ngOnInit(): void {
        for (const k of this.defaultKostenarten) {
            this.suffixBySachkonto[k.sachkonto] = '';
        }
        const f = this.data?.honorarTa;
        if (f?.fibu_honorar_ta_id) {
            this.isEdit = true;
            this.fibuHonorarTaId = f.fibu_honorar_ta_id;
            this.bezeichnungCtrl.setValue(f.bezeichnung);
            this.suffixCtrl.setValue(f.kostenstelle_suffix);
            this.aktivCtrl.setValue(f.aktiv > 0);
            this.kommentarCtrl.setValue(f.kommentar);
            if (f.tierarzt_id) {
                const t = new Tierarzt();
                t.tierarzt_id = f.tierarzt_id;
                t.titel = f.tierarzt_titel || '';
                t.vorname = f.tierarzt_vorname || '';
                t.nachname = f.tierarzt_nachname || (f.tierarzt || '').trim();
                this.tierarzt = t;
            }
            const list = f.kostenarten?.length ? f.kostenarten : this.parseMap(f.kostenarten_map);
            for (const ka of list) {
                this.suffixBySachkonto[ka.sachkonto] = ka.kostenstelle_suffix;
            }
            const main = (f.kostenstelle_suffix || '').trim();
            for (const k of this.defaultKostenarten) {
                if (!this.suffixBySachkonto[k.sachkonto] && main) {
                    this.suffixBySachkonto[k.sachkonto] = main;
                }
            }
            this.refreshKontoStatus();
        } else {
            this.suffixCtrl.valueChanges.subscribe(v => {
                const main = (v || '').trim();
                if (!main) {
                    return;
                }
                for (const k of this.defaultKostenarten) {
                    if (!this.suffixBySachkonto[k.sachkonto]) {
                        this.suffixBySachkonto[k.sachkonto] = main;
                    }
                }
            });
        }
    }

    get sachkontoKeys(): string[] {
        return Object.keys(this.suffixBySachkonto).sort((a, b) => a.localeCompare(b, 'de'));
    }

    labelFor(sachkonto: string): string {
        return this.defaultKostenarten.find(k => k.sachkonto === sachkonto)?.label || sachkonto;
    }

    setTierarzt(t: Tierarzt) {
        this.tierarzt = t;
    }

    addExtraKostenart() {
        const sach = (this.extraSachkontoCtrl.value || '').trim();
        const suf = (this.extraSuffixCtrl.value || '').trim() || (this.suffixCtrl.value || '').trim();
        if (!sach || !suf) {
            return;
        }
        this.suffixBySachkonto[sach] = suf;
        this.extraSachkontoCtrl.setValue('');
        this.extraSuffixCtrl.setValue('');
        this.refreshKontoStatus();
    }

    removeKostenart(sachkonto: string) {
        delete this.suffixBySachkonto[sachkonto];
        delete this.kontoStatus[sachkonto];
    }

    onSuffixBlur() {
        this.refreshKontoStatus();
    }

    isValid(): boolean {
        return this.bezeichnungCtrl.valid && this.suffixCtrl.valid;
    }

    save() {
        if (!this.isValid()) {
            return;
        }
        const kostenarten: FibuHonorarKostenart[] = [];
        for (const sach of this.sachkontoKeys) {
            const suf = (this.suffixBySachkonto[sach] || '').trim();
            if (suf) {
                kostenarten.push({ sachkonto: sach, kostenstelle_suffix: suf });
            }
        }
        const body: any = {
            bezeichnung: (this.bezeichnungCtrl.value || '').trim(),
            kostenstelle_suffix: (this.suffixCtrl.value || '').trim(),
            tierarzt_id: this.tierarzt?.tierarzt_id || null,
            aktiv: this.aktivCtrl.value ? 1 : 0,
            kommentar: (this.kommentarCtrl.value || '').trim() || null,
            kostenarten,
        };
        if (this.fibuHonorarTaId) {
            body.fibu_honorar_ta_id = this.fibuHonorarTaId;
        }

        this.api.post(this.apiBaseUrl + 'fibu_honorar_ta/update.php', body).subscribe({
            next: () => {
                this.messageService.infoMessage('Honorar-TA gespeichert.');
                this.dialogRef.close(true);
            },
            error: err => this.messageService.errorMessage('Speichern fehlgeschlagen.', err),
        });
    }

    cancel() {
        this.dialogRef.close(false);
    }

    private parseMap(map?: string): FibuHonorarKostenart[] {
        if (!map) {
            return [];
        }
        return map
            .split(';')
            .map(p => p.trim())
            .filter(Boolean)
            .map(p => {
                const [sachkonto, kostenstelle_suffix] = p.split('=');
                return {
                    sachkonto: (sachkonto || '').trim(),
                    kostenstelle_suffix: (kostenstelle_suffix || '').trim(),
                };
            })
            .filter(x => x.sachkonto && x.kostenstelle_suffix);
    }

    private refreshKontoStatus() {
        this.kontoStatus = {};
        for (const sach of this.sachkontoKeys) {
            const suf = (this.suffixBySachkonto[sach] || '').trim();
            if (!suf) {
                continue;
            }
            const params = new HttpParams()
                .set('aktiv', '1')
                .set('sachkonto', sach)
                .set('kostenstelle_suffix', suf)
                .set('limit', '1');
            this.api.get(this.apiBaseUrl + 'fibu_konto/read.php', params).subscribe({
                next: (res: any) => {
                    const hit = (res?.records || [])[0];
                    this.kontoStatus[sach] = hit ? `✓ ${hit.konto_nr}` : `fehlt: ${sach}.${suf}`;
                },
                error: () => {
                    this.kontoStatus[sach] = '?';
                },
            });
        }
    }
}
